import {Component, EventEmitter, inject, Input, OnDestroy, Output, TemplateRef, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {NgbModal, NgbModalOptions, NgbModalRef} from "@ng-bootstrap/ng-bootstrap";
import {NgTemplateOutlet} from "@angular/common";
import {I18nService} from "../../services/i18n/i18n.service";
import {TranslatePipe} from "@ngx-translate/core";
import {SpinnerComponent} from "../spinner/spinner.component";
import {ModalConfig} from "./modal.config";
import {ButtonComponent} from "../button/button.component";
import {debounceTime, EMPTY, merge, Subscription} from "rxjs";
import {isDefined, unsubscribe} from "../utils/epic.utils";
import {IconComponent} from "../icon/icon.component";

@Component({
  selector: 'acc-modal',
  imports: [
    TranslatePipe,
    NgTemplateOutlet,
    SpinnerComponent,
    ButtonComponent,
    IconComponent
  ],
  templateUrl: './modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './modal.component.css'
})
export class ModalComponent implements OnDestroy {

  @ViewChild('deleteTpl')
  protected deleteTpl: TemplateRef<any> | undefined;

  @ViewChild('inputTpl')
  protected inputTpl: TemplateRef<any> | undefined;

  @ViewChild('showTpl')
  protected showTpl: TemplateRef<any> | undefined;

  @ViewChild('inputConfirmButton')
  protected inputConfirmButton: ButtonComponent | undefined;

  @ViewChild('inputCancelButton')
  protected inputCancelButton: ButtonComponent | undefined;

  @Input({required: true})
  public type: 'delete' | 'input' | 'show' = 'delete';

  @Input()
  public action: 'process' | 'cancel' | 'wait' = 'process';

  @Input()
  public title?: string;

  @Input()
  public titleIconName?: string;

  @Input()
  public body?: string;

  @Input()
  public footer?: 'delete' | 'none';

  @Input()
  public options?: NgbModalOptions;

  @Input()
  public config: ModalConfig = new ModalConfig();

  @Output()
  public accConfirmed = new EventEmitter<any>();

  @Output()
  public accCanceled = new EventEmitter<any>();

  @Output()
  public accClosed = new EventEmitter<any>();

  @Output()
  public accOpened = new EventEmitter<any>();

  private readonly modalService = inject(NgbModal);
  private readonly i18n = inject(I18nService);
  private readonly subs: Subscription[] = [];
  private modalRef: NgbModalRef | undefined;
  private opened = false;

  ngOnDestroy(): void {
    this.close();
  }

  public open(): Promise<boolean> {
    this.setDefaults();
    return new Promise(resolve => {
      switch (this.type) {
        case 'delete':
          this.modalRef = this.modalService.open(this.deleteTpl, this.options);
          break;
        case 'input':
          this.modalRef = this.modalService.open(this.inputTpl, this.options);
          break;
        case 'show':
          this.modalRef = this.modalService.open(this.showTpl, this.options);
          break;
      }
      this.opened = true;
      this.subs.push(
        merge(this.modalRef?.closed || EMPTY, this.modalRef?.dismissed || EMPTY)
          .pipe(debounceTime(100))
          .subscribe((e) => this.accClosed.emit(e))
      );
      setTimeout(() => {
        resolve(this.opened);
        this.accOpened.emit(this.opened);
      }, 10);
    });
  }

  public isOpen(): boolean {
    return this.opened;
  }

  public close(): void {
    if (this.opened) {
      this.opened = false;
      this.modalRef?.close();
    }
    unsubscribe(this.subs);
  }

  public getInputConfirmButton(): ButtonComponent | undefined {
    return this.inputConfirmButton;
  }

  public getInputCancelButton(): ButtonComponent | undefined {
    return this.inputCancelButton;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  protected onConfirm(event: any): void {
    this.accConfirmed.emit(event);
    if (this.config.autoClose) {
      this.close();
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  protected onCancel(event: any): void {
    this.accCanceled.emit(event);
    this.modalRef?.dismiss();
    if (this.config.autoClose) {
      this.close();
    }
  }

  private setDefaults(): void {
    switch (this.type) {
      case 'delete':
        this.title = this.title || this.i18n.translate('modal.delete.title');
        this.body = this.body || this.i18n.translate('modal.delete.body');
        this.footer = this.footer || 'delete';
        break;
      case 'input':
      case 'show':
        break;
    }
    this.options = this.buildOptions(this.options);
  }

  private buildOptions(options?: NgbModalOptions): NgbModalOptions {
    return {
      size: isDefined(options?.size) ? options?.size : undefined,
      centered: isDefined(options?.centered) ? options?.centered : true,
      backdrop: isDefined(options?.backdrop) ? options?.backdrop : true,
      keyboard: isDefined(options?.keyboard) ? options?.keyboard : true,
      windowClass: isDefined(options?.windowClass) ? options?.windowClass : undefined,
    };
  }
}
